import { UserService } from "services/user.service";
import { OrganizationService } from "./../services/organization.service";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoginUserDetail } from "models/LoginUserDetail";
import { RootState } from "./store";
import { Shared } from "utility/shared";
import { IMenuAccessRightModel } from "models/MenuAccessRightModel";
import { OrganizationDetail } from "models/OrganizationDetail";
import { AppRoutings } from "utility/enums/app-routings";

export const defaultRegionGridLabel = "Region";
export const defaultDivisionGridLabel = "Division";
export const defaultSubRegionGridLabel = "Sub-Region";
export const defaultSubDivisionGridLabel = "Sub-Division";
export const defaultSubDepartmentGridLabel = "Sub-Department";
export const defaultDepartmentGridLabel = "Department";
export const defaultProgrammetGridLabel = "Programme";
export const defaultProjectGridLabel = "Project";
export const defaultCostCategoryGridLabel = "Cost-Category";

export interface AuthState {
	userDetails: LoginUserDetail;
	organizationDetails: OrganizationDetail;
	userMenuAccessRights: IMenuAccessRightModel[];
	gridLabel: {
		region: string;
		division: string;
		subDivision: string;
		subRegion: string;
		subDepartment: string;
		department: string;
		programme: string;
		project: string;
		costCategory: string;
	};
}

const initialState: AuthState = {
	userDetails: {} as LoginUserDetail,
	organizationDetails: {} as OrganizationDetail,
	userMenuAccessRights: [],
	gridLabel: {
		region: "",
		division: "",
		subDivision: "",
		subRegion: "",
		subDepartment: "",
		department: "",
		programme: "",
		project: "",
		costCategory: "",
	},
};

export const fetchOrganizationDetail = createAsyncThunk(
	"getOrganizationDetails",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (!state.authState.organizationDetails.organizationId || isForceAPICall) {
			return await OrganizationService.getOrganizationDetails();
		}
	}
);

export const fetchUserMenuAccessRights = createAsyncThunk(
	"getUserMenuAccessRights",
	async (isForceAPICall: boolean, { getState }) => {
		const state = getState() as RootState;
		if (state.authState.userMenuAccessRights.length === 0 || isForceAPICall) {
			return await UserService.getUserMenuAccessRights();
		}
	}
);

export const authStateSlice = createSlice({
	name: "authState",
	initialState,
	reducers: {
		setLoggedInUserDetail: (state, action) => {
			state.userDetails = action.payload as LoginUserDetail;
		},
		setOrganizationDetail: (state, action) => {
			state.organizationDetails = action.payload as OrganizationDetail;
			state.gridLabel =
				action.payload &&
				action.payload.glAccountSettings &&
				action.payload.glAccountSettings.length > 0
					? {
							region:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "region"
								)?.label || defaultRegionGridLabel,
							division:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "division"
								)?.label || defaultDivisionGridLabel,
							subDivision:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "sub-division"
								)?.label || defaultSubDivisionGridLabel,
							subRegion:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "sub-region"
								)?.label || defaultSubRegionGridLabel,
							subDepartment:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "sub-department"
								)?.label || defaultSubDepartmentGridLabel,
							department:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "department"
								)?.label || defaultDepartmentGridLabel,
							programme:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "programme"
								)?.label || defaultProgrammetGridLabel,
							project:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "project"
								)?.label || defaultProjectGridLabel,
							costCategory:
								action.payload.glAccountSettings.find(
									(s: any) => s.name.toLowerCase() === "cost-category"
								)?.label || defaultCostCategoryGridLabel,
					  }
					: {
							region: defaultRegionGridLabel,
							division: defaultDivisionGridLabel,
							subDivision: defaultSubDivisionGridLabel,
							subRegion: defaultSubRegionGridLabel,
							subDepartment: defaultSubDepartmentGridLabel,
							department: defaultDepartmentGridLabel,
							programme: defaultProgrammetGridLabel,
							project: defaultProjectGridLabel,
							costCategory: defaultCostCategoryGridLabel,
					  };
		},
	},
	extraReducers: {
		[fetchOrganizationDetail.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.organizationId) {
				state.organizationDetails = action.payload;
				state.gridLabel =
					action.payload &&
					action.payload.glAccountSettings &&
					action.payload.glAccountSettings.length > 0
						? {
								region:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "region"
									)?.label || defaultRegionGridLabel,
								division:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "division"
									)?.label || defaultDivisionGridLabel,
								subDivision:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "sub-division"
									)?.label || defaultSubDivisionGridLabel,
								subRegion:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "sub-region"
									)?.label || defaultSubRegionGridLabel,
								subDepartment:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "sub-department"
									)?.label || defaultSubDepartmentGridLabel,
								department:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "department"
									)?.label || defaultDepartmentGridLabel,
								programme:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "programme"
									)?.label || defaultProgrammetGridLabel,
								project:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "project"
									)?.label || defaultProjectGridLabel,
								costCategory:
									action.payload.glAccountSettings.find(
										(s: any) => s.name.toLowerCase() === "cost-category"
									)?.label || defaultCostCategoryGridLabel,
						  }
						: {
								region: defaultRegionGridLabel,
								division: defaultDivisionGridLabel,
								subDivision: defaultSubDivisionGridLabel,
								subRegion: defaultSubRegionGridLabel,
								subDepartment: defaultSubDepartmentGridLabel,
								department: defaultDepartmentGridLabel,
								programme: defaultProgrammetGridLabel,
								project: defaultProjectGridLabel,
								costCategory: defaultCostCategoryGridLabel,
						  };
				localStorage.setItem(
					Shared.localStorageKeys.organizationDetails,
					JSON.stringify(action.payload)
				);
			}
		},
		[fetchOrganizationDetail.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error loading organization detail, please try again!"
			);
		},
		[fetchUserMenuAccessRights.fulfilled.type]: (state, action) => {
			if (action.payload && action.payload.length > 0) {
				state.userMenuAccessRights = action.payload;
			}
		},
		[fetchUserMenuAccessRights.rejected.type]: (_state, _action) => {
			Shared.showSnackbarMessage(
				"Error while fetching user menu access right list, please try again!"
			);
			// added timeout to display snackbar message before redirection
			setTimeout(() => {
				window.location.href = AppRoutings.SignIn;
			}, 1000);
		},
	},
});

export const { setLoggedInUserDetail, setOrganizationDetail } =
	authStateSlice.actions;

export default authStateSlice.reducer;
